import {
  GATE_DIAMETER,
  NODE_BODY_HEIGHT,
  NODE_CAP_RADIUS,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import { isGateNode } from './boardItem'

export type AnchorName = 'top' | 'right' | 'bottom' | 'left'

export const getNodeTopAttachmentY = (item: { position: { y: number }; nodeKind?: string }) =>
  isGateNode(item) ? item.position.y : item.position.y - NODE_CAP_RADIUS

export const getNodeBottomAttachmentY = (item: { position: { y: number }; nodeKind?: string }) =>
  isGateNode(item) ? item.position.y + GATE_DIAMETER : item.position.y + NODE_BODY_HEIGHT + NODE_CAP_RADIUS

export const getAnchorPoint = (item: BoardChecklistItem, anchor: AnchorName) => {
  const gate = isGateNode(item)
  const width = gate ? GATE_DIAMETER : NODE_WIDTH
  switch (anchor) {
    case 'top':
      return { x: item.position.x + width / 2, y: getNodeTopAttachmentY(item) }
    case 'right':
      return { x: item.position.x + width, y: item.position.y + (gate ? GATE_DIAMETER : NODE_BODY_HEIGHT) / 2 }
    case 'bottom':
      return { x: item.position.x + width / 2, y: getNodeBottomAttachmentY(item) }
    case 'left':
      return { x: item.position.x, y: item.position.y + (gate ? GATE_DIAMETER : NODE_BODY_HEIGHT) / 2 }
  }
}

export type WireEndpointSlot = { index: number; count: number }

export function getNodeWireEndpoint(
  itemToRender: BoardChecklistItem,
  role: 'in' | 'out',
  slot?: WireEndpointSlot,
): { x: number; y: number } {
  const gate = isGateNode(itemToRender)
  const capRadius = gate ? GATE_DIAMETER / 2 : NODE_CAP_RADIUS
  const centerX = itemToRender.position.x + (gate ? GATE_DIAMETER / 2 : NODE_WIDTH / 2)
  const capApexY = role === 'in'
    ? itemToRender.position.y - (gate ? 0 : NODE_CAP_RADIUS)
    : itemToRender.position.y + (gate ? GATE_DIAMETER : NODE_BODY_HEIGHT + NODE_CAP_RADIUS)

  if (!slot || slot.count <= 1) {
    return { x: centerX, y: capApexY }
  }

  const MAX_ANGLE = Math.PI / 3 // +/- 60 deg
  const step = (2 * MAX_ANGLE) / (slot.count - 1)
  const theta = -MAX_ANGLE + step * slot.index
  const dir = role === 'in' ? 1 : -1
  return {
    x: centerX + Math.sin(theta) * capRadius,
    y: capApexY + dir * (1 - Math.cos(theta)) * capRadius,
  }
}

/**
 * Checklist Drawing Board UI Redesign Contract:
 * Adding a wire from source to target updates `target.prerequisites[]`.
 * Multi-prerequisite semantics on the drawing board is strictly AND: every
 * prerequisite in `prerequisites[]` must reach 100% completion before the target
 * unlocks. GATE_OR is reachable ONLY by explicitly adding a GATE_OR node.
 * Never insert a synthetic GATE_OR when wiring multiple prerequisites directly.
 */
export function assertAndSemanticsForPrereqs(sourceId: string, target: BoardChecklistItem): void {
  if (!sourceId || !target) {
    throw new Error('assertAndSemanticsForPrereqs: sourceId and target must be defined')
  }
}

export const isGateConnected = (
  node: Pick<BoardChecklistItem, 'id' | 'gateInputIds'>,
  displayNodes: Array<Pick<BoardChecklistItem, 'id' | 'prerequisites'>>,
): boolean => {
  const inputs = node.gateInputIds ?? []
  const hasOutgoing = displayNodes.some((c) => c.prerequisites.includes(node.id))
  return inputs.length >= 2 && inputs.length <= 4 && hasOutgoing
}

export const sampleCubicConnectorPath = (path: string, scale: number, scrollLeft: number, scrollTop: number): Array<{ x: number; y: number }> => {
  const values = path.match(/-?\d+(?:\.\d+)?/g)?.map(Number) ?? []
  if (values.length < 8) return []
  const [x0, y0, x1, y1, x2, y2, x3, y3] = values
  const points: Array<{ x: number; y: number }> = []
  for (let step = 0; step <= 20; step += 1) {
    const t = step / 20
    const mt = 1 - t
    const x = (mt ** 3 * x0) + (3 * mt ** 2 * t * x1) + (3 * mt * t ** 2 * x2) + (t ** 3 * x3)
    const y = (mt ** 3 * y0) + (3 * mt ** 2 * t * y1) + (3 * mt * t ** 2 * y2) + (t ** 3 * y3)
    points.push({ x: x * scale - scrollLeft, y: y * scale - scrollTop })
  }
  return points
}

export const toKnifePoint = (point: { x: number; y: number }, scale: number, scrollLeft: number, scrollTop: number) => ({
  x: point.x * scale - scrollLeft,
  y: point.y * scale - scrollTop,
})
